export async function safeFetchJSON(url, { signal } = {}) {
  const res = await fetch(url, { signal });
  const data = await res.json().catch(() => null);

  if (!res.ok) {
    throw new Error(
      data?.message || data?.error || `Request failed (${res.status})`,
    );
  }

  return data;
}

export function normalizeHazards(hazards) {
  if (!Array.isArray(hazards)) return "";

  return hazards
    .map((hazard) => hazard?.hazards || hazard?.name || hazard)
    .filter(Boolean)
    .join(", ");
}

export function parseDateTimeBoundary(input, endOfDay = false) {
  if (!input) return null;

  const value = String(input).trim();
  const hasTime = /T|\d{1,2}:\d{2}/.test(value);
  const date = new Date(
    hasTime ? value : `${value}T${endOfDay ? "23:59:59" : "00:00:00"}`,
  );

  return Number.isNaN(date.getTime()) ? null : date;
}

export function getOpenStartDate(item) {
  return item?.openedAt || item?.date || item?.createdAt || null;
}

export function getAssigneeIds(item) {
  const list = Array.isArray(item?.assignedTo)
    ? item.assignedTo
    : item?.assignedTo
      ? [item.assignedTo]
      : [];

  return list
    .map((assignee) =>
      typeof assignee === "object" ? assignee?._id : assignee,
    )
    .filter(Boolean);
}

export function getAssigneeNameById(item, id) {
  const list = Array.isArray(item?.assignedTo)
    ? item.assignedTo
    : item?.assignedTo
      ? [item.assignedTo]
      : [];

  const found = list.find((assignee) => {
    const assigneeId = typeof assignee === "object" ? assignee?._id : assignee;
    return String(assigneeId || "") === String(id || "");
  });

  return typeof found === "object" ? found?.name || found?.userId || "" : "";
}

export function getAssigneeObjById(item, id) {
  const list = Array.isArray(item?.assignedTo)
    ? item.assignedTo
    : item?.assignedTo
      ? [item.assignedTo]
      : [];

  const found = list.find((assignee) => {
    const assigneeId = typeof assignee === "object" ? assignee?._id : assignee;
    return String(assigneeId || "") === String(id || "");
  });

  return typeof found === "object" ? found : null;
}

export function isClosedStatus(status) {
  return /closed|resolved/i.test(String(status || ""));
}

export function getSlaHours(item) {
  const risk = item?.riskValue || {};
  const base = Number(risk.alertTimeline) || 0;
  const escalations = Array.isArray(risk.escalationAlert)
    ? risk.escalationAlert.length
    : 0;

  return base * (escalations || 1);
}

export function getDueAt(item) {
  const opened = getOpenStartDate(item);
  const hours = getSlaHours(item);

  if (!opened || !hours) return null;

  return new Date(new Date(opened).getTime() + hours * 60 * 60 * 1000);
}

export function getDueMeta(item) {
  const opened = getOpenStartDate(item);
  const dueAt = getDueAt(item);

  if (!opened || !dueAt) {
    return { dueAt: null, overdue: false, closedLate: false };
  }

  if (isClosedStatus(item?.status)) {
    const closedAt =
      item?.closedOn || item?.closedAt || item?.resolvedAt || item?.updatedAt;

    return {
      dueAt,
      overdue: false,
      closedLate: closedAt
        ? new Date(closedAt).getTime() > dueAt.getTime()
        : false,
    };
  }

  return {
    dueAt,
    overdue: Date.now() > dueAt.getTime(),
    closedLate: false,
  };
}

export function formatDuration(ms) {
  if (!Number.isFinite(ms)) return "-";

  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}
